import type { GatewayBrowserClient } from "../api/gateway.ts";
import { listen } from "../lib/reactive/listener.ts";
import type { ApplicationGatewayPhase } from "./gateway.ts";

type PageActivationGateway = {
  readonly snapshot: {
    readonly client: Pick<GatewayBrowserClient, "needsWakeReconnect"> | null;
    readonly phase: ApplicationGatewayPhase;
  };
  connect: () => void;
};

/** Revalidates process-backed browser state after Safari resumes a suspended tab. */
export function startGatewayPageActivation(
  gateway: PageActivationGateway,
  documentTarget: Document,
  windowTarget: Window,
): () => void {
  let disposed = false;
  let queued = false;
  let forceRecovery = false;
  let wasHidden = documentTarget.visibilityState === "hidden";

  const schedule = (force: boolean) => {
    if (documentTarget.visibilityState === "hidden") {
      return;
    }
    forceRecovery ||= force;
    if (queued) {
      return;
    }
    queued = true;
    queueMicrotask(() => {
      queued = false;
      const forceQueuedRecovery = forceRecovery;
      forceRecovery = false;
      if (disposed || documentTarget.visibilityState === "hidden") {
        return;
      }
      const { client, phase } = gateway.snapshot;
      if (
        client &&
        !["stopped", "offline", "reload-required"].includes(phase) &&
        (forceQueuedRecovery || client.needsWakeReconnect)
      ) {
        // Replacing the client retires Safari's ghost socket synchronously. A
        // socket close would depend on the suspended transport firing `close`.
        gateway.connect();
      }
      void import("./sw-refresh.runtime.ts")
        .then(({ refreshControlUiServiceWorker }) => refreshControlUiServiceWorker())
        .catch((error: unknown) => {
          console.warn("OpenClaw service worker refresh failed after page activation.", error);
        });
    });
  };
  const onVisibilityChange = () => {
    if (documentTarget.visibilityState === "hidden") {
      wasHidden = true;
      return;
    }
    if (wasHidden) {
      wasHidden = false;
      schedule(false);
    }
  };
  const onPageShow = (event: PageTransitionEvent) => {
    if (event.persisted) {
      schedule(true);
    }
  };
  const onOnline = () => schedule(false);

  const stops = [
    listen(documentTarget, "visibilitychange", onVisibilityChange),
    listen(windowTarget, "pageshow", onPageShow),
    listen(windowTarget, "online", onOnline),
  ];
  return () => {
    disposed = true;
    stops.forEach((stop) => stop());
  };
}
